// Wave 20.37 — Capability flags per deployment surface.
//
// Historically the codebase branched on `isRaphaDomain()` at every
// place a commercial-only feature needed hiding. That made features
// creep into Rapha silently (drops, mobile-app promos, follow surfaces
// all leaked because nobody remembered to add a guard).
//
// This file is the single source of truth. Anywhere UI should differ
// by tier — commercial vs. rapha vs. anything else we ship — read a
// specific capability off `useFeatures()`, not the subdomain.
//
// Rules:
//   * Never grep `IS_RAPHA` outside this file. If you need to gate a
//     surface, add a capability here.
//   * Capabilities describe INTENT ("this surface shows drops"), not
//     mechanism ("we're on rapha domain"). Same capability may be
//     off on multiple tiers in future (paid tiers, team plans, kiosk
//     mode, etc.).

import { useMemo } from 'react';
import { isRaphaDomain } from './subdomain';

export interface Features {
  /** Drop links — send + receive peak / activity / session drops. */
  drops: boolean;
  /** Social Feed surfaces — Following tab, LiveFriends, DropInbox,
   *  peer activity cards from other users. */
  socialFeed: boolean;
  /** /discover route + any "find people to follow" affordance. */
  discoverPeople: boolean;
  /** Other users' public profile pages (`/u/:username`). */
  otherUserProfiles: boolean;
  /** Any "get the mobile app" promo, download link, QR code, etc. */
  mobileAppPromo: boolean;
  /** Feed sidebar weather panel + any /weather route. */
  weatherPanel: boolean;
  /** Feed sidebar positions panel + /analytics route. */
  positionsPanel: boolean;
  /** Indoor camera-based aero session (Preflight → AIClientVision). */
  aeroSession: boolean;
  /** Outdoor recorded sessions (`/activities`, session details for
   *  rides / runs — not in-store trainer sessions). */
  outdoorSessions: boolean;
  /** 3D terrain rendering on the session detail route map. Uses
   *  self-hosted terrain-RGB tiles from terrain.aero.chat. Off by
   *  default while we validate the tile pipeline + battery cost. */
  terrain3D: boolean;
  /** Wave 20.43 — Aero Wake landing screen (post-auth default on
   *  commercial). Full-bleed live particle animation representing
   *  the rider's evolved aerodynamic performance over time. */
  wake: boolean;
}

const COMMERCIAL: Features = {
  drops: true,
  socialFeed: true,
  discoverPeople: true,
  otherUserProfiles: true,
  mobileAppPromo: true,
  weatherPanel: true,
  positionsPanel: true,
  aeroSession: true,
  outdoorSessions: true,
  terrain3D: true,
  wake: true,
};

const RAPHA: Features = {
  drops: false,
  socialFeed: false,
  discoverPeople: false,
  otherUserProfiles: false,
  mobileAppPromo: false,
  weatherPanel: true,
  positionsPanel: true,
  aeroSession: true,
  outdoorSessions: false,
  terrain3D: false,
  wake: false,
};

/** Snapshot at import time (subdomain doesn't change during a session).
 *  Exposed for non-React code that needs a synchronous read. */
export function currentFeatures(): Features {
  return isRaphaDomain() ? RAPHA : COMMERCIAL;
}

/** React hook — memoized so consumers don't re-render on identity. */
export function useFeatures(): Features {
  return useMemo(() => currentFeatures(), []);
}
